import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { TradeItem, InventoryItem, type TradeListParams } from '@/types';

interface TradesDataStore {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredTradesData: TradeItem[];
  setFilteredTradesData: (data: TradeItem[]) => void;
  /** Trades after view + mode scoping but BEFORE sidebar/search filters.
   *  Used as the stable basis for category counters (All / Stock L/B / Repo)
   *  in MetricsBar — keeps them independent of the active tradeTypes filter. */
  viewScopedTradesData: TradeItem[];
  /** True once a page has published `viewScopedTradesData` (even an empty set).
   *  Distinguishes a genuinely EMPTY view — honest zero metrics — from "not
   *  synced yet", where use-view-metrics falls back to `filteredTradesData`
   *  (unit tests rendering the bar alone rely on that fallback) — kvi #6. */
  viewScopedSynced: boolean;
  setViewScopedTradesData: (data: TradeItem[], key?: string | null) => void;
  /** Identity of the last published scoped set: `${viewId}|live` or
   *  `${viewId}|history`. TradesPage publishes in an EFFECT, one commit behind
   *  the props/store reads of children on a view switch (or a history→live
   *  remount) — consumers that ACT on the scoped set (MetricsBar's stranded-
   *  preset auto-clear, LR-518 r2 #0) must check the key matches their current
   *  view before trusting it. Display-only consumers can keep ignoring it. */
  viewScopedKey: string | null;
  /** Whole-book filter-option pools (from the server aggregate) so the sidebar
   *  dropdowns offer every value in the book, not just the loaded page. */
  symbolOptions: string[];
  currencyOptions: string[];
  setFilterOptionPools: (pools: { symbols: string[]; currencies: string[] }) => void;
  /** Server list params (buildTradesServerParams) + server total published by
   *  TradesPage so the sidebar CSV export can fetch the WHOLE filtered book (the
   *  list is windowed) and label the true row count — LR-546. */
  tradesListParams?: Omit<TradeListParams, 'limit' | 'offset'>;
  setTradesListParams: (params: Omit<TradeListParams, 'limit' | 'offset'>) => void;
  tradesTotalCount?: number;
  setTradesTotalCount: (count: number | undefined) => void;
  isTradesDataLoading: boolean;
  setTradesDataLoading: (loading: boolean) => void;
  tradesDataError: boolean;
  setTradesDataError: (error: boolean) => void;
  isCreateTradeModalOpen: boolean;
  setIsCreateTradeModalOpen: (open: boolean) => void;
  selectedItemForTrade: InventoryItem | null;
  setSelectedItemForTrade: (item: InventoryItem | null) => void;
  /** LR-671: blotter sort order. Lives here (persisted) instead of TradesPage
   *  `useState` so a saved view can capture it and the dirty check can track
   *  it — mirror of `inventorySorting` in inventory-data-store. */
  tradesSorting: { id: string; desc: boolean }[];
  setTradesSorting: (sorting: { id: string; desc: boolean }[]) => void;
}

export const useTradesDataStore = create<TradesDataStore>()(
  devtools(
    persist(
      (set) => ({
        searchQuery: '',
        filteredTradesData: [],
        viewScopedTradesData: [],
        viewScopedSynced: false,
        symbolOptions: [],
        currencyOptions: [],
        viewScopedKey: null,
        isTradesDataLoading: false,
        tradesDataError: false,
        isCreateTradeModalOpen: false,
        selectedItemForTrade: null,
        tradesSorting: [],
        setTradesSorting: (sorting) => set({ tradesSorting: sorting }),
        setSearchQuery: (query) => set({ searchQuery: query }),
        setFilteredTradesData: (data) => set({ filteredTradesData: data }),
        setViewScopedTradesData: (data, key) =>
          set({ viewScopedTradesData: data, viewScopedSynced: true, viewScopedKey: key ?? null }),
        setFilterOptionPools: (pools) => set({ symbolOptions: pools.symbols, currencyOptions: pools.currencies }),
        setTradesListParams: (params) => set({ tradesListParams: params }),
        setTradesTotalCount: (count) => set({ tradesTotalCount: count }),
        setTradesDataLoading: (loading) => set({ isTradesDataLoading: loading }),
        setTradesDataError: (error) => set({ tradesDataError: error }),
        setIsCreateTradeModalOpen: (open) => set({ isCreateTradeModalOpen: open }),
        setSelectedItemForTrade: (item) => set({ selectedItemForTrade: item }),
      }),
      {
        name: 'trades-data-store',
        version: 0,
        partialize: (state) => ({
          searchQuery: state.searchQuery,
          // Saved-views invariant (trades-adapter): every captured field is
          // persisted by its owning store, else F5 reads dirty (LR-671).
          tradesSorting: state.tradesSorting,
        }),
      }
    ),
    {
      name: 'trades-data-store', enabled: import.meta.env.DEV,
    }
  )
);

